import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Chip,
  ConfirmDialog,
  DataTable,
  DrawerPresence,
  ErrorBanner,
  IconButton,
  PAGE_SIZE,
  PageHeader,
  SearchCombobox,
  useToast,
  type DataTableColumn,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { t, tn } from '@app/i18n';
import { withEmphasis } from '@app/i18n/emphasis';
import { useDrawerParam } from '@app/navigation';
import {
  useActivatePolicy,
  useDeactivatePolicy,
  useDeletePolicy,
  usePolicies,
  usePoliciesSummary,
} from './Policies.api';
import {
  grantsText,
  claimsOverflow,
  modeShortLabel,
  stateLabel,
  FILTERS_INICIAIS,
  modeLabel,
  hasFilterApplied,
  type PoliciesFilters,
  type Policy,
  type PolicyState,
  type PolicyMode,
} from './PolicyModel';
import { PolicyDrawer, type PolicyDrawerTab } from './PolicyDrawer';
import s from './Policies.module.css';

/** Os quatro indicadores do topo (FR-087). */
function Indicators({
  ativas,
  rascunhos,
  cobertas,
  capabilities,
  regoManual,
}: {
  ativas?: number;
  rascunhos?: number;
  cobertas?: number;
  capabilities?: number;
  regoManual?: number;
}) {
  const cartoes = [
    {
      rotulo: t('humanAccess.kpi.active'),
      valor: ativas,
      // Detalhe numérico só existe se o número existir: com o resumo fora do ar,
      // "0 em rascunho" seria informação fabricada ao lado de um erro.
      detalhe: rascunhos == null ? null : t('humanAccess.kpi.active.detail', { n: rascunhos }),
      tom: 'pri' as const,
    },
    {
      rotulo: t('humanAccess.kpi.covered'),
      valor: cobertas,
      detalhe: t('humanAccess.kpi.covered.detail'),
      tom: 'ok' as const,
    },
    {
      rotulo: t('humanAccess.kpi.capabilities'),
      valor: capabilities,
      // «além do agent.yaml» era união (corrigido em 04/09, ensaio da demo): dizia que a
      // regra concede capability ALÉM do que o contrato declara — exatamente o modelo que a
      // reorganização Human × Identity Access removeu. A regra concede à PESSOA e nunca
      // alarga a identidade; o acesso efetivo é a interseção. E o rótulo aparecia projetado
      // nesta mesma tela no instante em que a cena 6 da demo diz «Human Access não alarga o
      // agente» — a tela contradizendo a fala.
      detalhe: t('humanAccess.kpi.capabilities.detail'),
      tom: 'pri' as const,
    },
    {
      rotulo: t('humanAccess.kpi.regoManual'),
      valor: regoManual,
      detalhe: t('humanAccess.kpi.regoManual.detail'),
      tom: 'wrn' as const,
    },
  ];

  return (
    <div className={s.kpis}>
      {cartoes.map((cartao) => {
        // Sem valor lido, sem cor: o tom afirma um estado, e não sabemos qual é.
        const withoutValue = cartao.valor == null;
        return (
          <Card key={cartao.rotulo} className={s.kpi}>
            <span className={s.kpiRotulo}>{cartao.rotulo}</span>
            <strong
              className={[s.kpiValor, withoutValue ? s.kpiSemValor : s[cartao.tom]].join(' ')}
              aria-label={
                withoutValue ? t('humanAccess.kpi.unread', { label: cartao.rotulo }) : undefined
              }
            >
              {withoutValue ? t('humanAccess.kpi.noValue') : cartao.valor}
            </strong>
            {cartao.detalhe && !withoutValue ? (
              <span className={s.kpiDetalhe}>{cartao.detalhe}</span>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}

/**
 * T11 — Políticas (Story 9).
 *
 * Política é o que se concede **além** do que cada agente traz na declaração da
 * release. A lista existe para responder três coisas de relance: o que está
 * publicado no PDP, quem é alcançado e o que exatamente foi concedido.
 */
type PendingAction = { tipo: 'desativar' | 'excluir'; politica: Policy } | null;

function impactLabel(howMany: number): string {
  // `tn` sem `{n}` na frase: o `ConfirmDialog` já mostra a contagem ao lado do
  // rótulo, e repeti-la diria «2 2 identidades…».
  return tn('humanAccess.confirm.impact', howMany);
}

export function PoliciesPage() {
  const [filters, setFiltros] = useState<PoliciesFilters>(FILTERS_INICIAIS);
  const drawer = useDrawerParam();
  const toast = useToast();

  const lista = usePolicies(filters);
  const summary = usePoliciesSummary();
  const canCreate = useCanOperar('createPolicy');
  const canEdit = useCanOperar('updatePolicy');
  const canActivate = useCanOperar('activatePolicy');
  const canDeactivate = useCanOperar('deactivatePolicy');
  const canDelete = useCanOperar('deletePolicy');

  // Ações da linha (ajustes-front #10): Editar abre T12; Ativar publica direto
  // (é o que o toggle do drawer faz); Desativar e Excluir tiram concessões do
  // PDP, então passam pela confirmação com contagem (FR-130), como no drawer.
  const activate = useActivatePolicy();
  const deactivate = useDeactivatePolicy();
  const remove = useDeletePolicy();
  const [acao, setAcao] = useState<PendingAction>(null);
  /**
   * Aba em que o drawer abre. «Geral» sempre, menos pelo chip «definida em Rego»
   * da coluna «Quem pede»: quem clica ali quer ler a expressão, e mandá-lo para
   * Geral obrigaria um segundo clique para chegar onde a informação está.
   */
  const [drawerTab, setDrawerTab] = useState<PolicyDrawerTab>('geral');

  function openRegoTab(id: string) {
    setDrawerTab('rego');
    drawer.abrir(id);
  }

  function openDrawer(id: string) {
    setDrawerTab('geral');
    drawer.abrir(id);
  }

  function activatePolicy(politica: Policy) {
    activate.mutate(
      { id: politica.id, version: politica.version },
      {
        onSuccess: (ativa) =>
          toast.mostrar(
            t('humanAccess.toast.published', { name: ativa.name, version: ativa.version }),
            { tone: 'sucesso' },
          ),
      },
    );
  }

  function confirmAction() {
    if (!acao) return;
    const { politica } = acao;
    if (acao.tipo === 'desativar') {
      deactivate.mutate(
        { id: politica.id, version: politica.version },
        {
          onSuccess: () => {
            setAcao(null);
            toast.mostrar(t('humanAccess.toast.nowDraft', { name: politica.name }), {
              tone: 'info',
            });
          },
        },
      );
      return;
    }
    remove.mutate(politica.id, {
      onSuccess: () => {
        setAcao(null);
        toast.mostrar(t('humanAccess.toast.deleted', { name: politica.name }), { tone: 'info' });
      },
    });
  }

  const itens = lista.data?.items ?? [];
  const filtrado = hasFilterApplied(filters);

  function swapFilters(mudanca: Partial<PoliciesFilters>) {
    setFiltros((current) => ({ ...current, page: 1, ...mudanca }));
  }

  const columns: DataTableColumn<Policy>[] = [
    {
      key: 'name',
      header: t('humanAccess.col.name'),
      sortable: true,
      // O nome deixou de ser o link de edição (ajustes-front #10): quem abre
      // T12 é o **Editar** da coluna Ações.
      cell: (politica) => (
        <span className={s.nome}>
          <strong>{politica.name}</strong>
          {politica.description ? (
            <span className={s.descricao}>{politica.description}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'conditions',
      header: t('humanAccess.col.who'),
      tip: t('humanAccess.col.who.tip'),
      // OS DOIS CASOS-LIMITE DA COLUNA (spec «Acesso: pessoa × agente» §4)
      //
      //   Muitas claims → as duas primeiras e um chip «+n», cujo hover lista as
      //   escondidas. A coluna nunca quebra em duas linhas, e a busca (que é do
      //   servidor) continua procurando dentro do que o chip esconde.
      //
      //   Rego manual → as condições são expressão livre, e não há resumo de
      //   claims honesto para derivar. A coluna diz «definida em Rego» e o clique
      //   abre o drawer na aba Rego. Resumir a expressão seria pior que não
      //   mostrar nada: um resumo errado de regra de acesso decide errado.
      cell: (politica) => {
        if (politica.mode === 'rego') {
          return (
            <button
              type="button"
              className={s.regoChip}
              title={t('humanAccess.rego.chipTitle')}
              onClick={() => openRegoTab(politica.id)}
            >
              {t('humanAccess.rego.chip')}
            </button>
          );
        }
        const { visible, hidden } = claimsOverflow(politica.conditions);
        if (!visible.length)
          return <span className={s.vazio}>{t('humanAccess.conditions.none')}</span>;
        return (
          <span className={s.claims}>
            <span className={s.claimsVisible}>{visible.join(' · ')}</span>
            {hidden.length ? (
              <span
                className={s.mais}
                title={hidden.join(' · ')}
                aria-label={t('humanAccess.claims.more', {
                  n: hidden.length,
                  list: hidden.join(' · '),
                })}
              >
                +{hidden.length}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'identities',
      header: t('humanAccess.col.identities'),
      tip: t('humanAccess.col.identities.tip'),
      cell: (politica) =>
        politica.all_identities ? (
          // «todas» não é "muitas identidades": é as que existem e as que vierem.
          // Listar chips aqui esconderia a herança atrás de uma lista de hoje.
          <Chip icon="robot">{t('humanAccess.identities.all')}</Chip>
        ) : politica.identities.length ? (
          <span
            className={s.mono}
            title={politica.identities.map((identidade) => identidade.slug).join(' · ')}
          >
            {politica.identities.length}
          </span>
        ) : (
          <span className={s.vazio}>{t('humanAccess.identities.none')}</span>
        ),
    },
    {
      key: 'concede',
      header: t('humanAccess.col.asserts'),
      tip: t('humanAccess.col.asserts.tip'),
      // Em Rego manual a coluna é «—»: o módulo só **restringe**, então não há
      // concessão para contar.
      cell: (politica) =>
        politica.mode === 'rego' ? (
          <span className={s.vazio} title={t('humanAccess.rego.onlyRestricts')}>
            {t('humanAccess.identities.none')}
          </span>
        ) : (
          grantsText(politica)
        ),
    },
    {
      key: 'modo',
      header: t('humanAccess.col.mode'),
      width: '120px',
      cell: (politica) => (
        <Badge
          tone={politica.mode === 'rego' ? 'warning' : 'primary'}
          tip={modeLabel(politica.mode)}
        >
          {modeShortLabel(politica.mode)}
        </Badge>
      ),
    },
    {
      key: 'estado',
      header: t('humanAccess.col.state'),
      width: '150px',
      cell: (politica) => (
        <Badge tone={politica.state === 'ativa' ? 'success' : 'neutral'}>
          {stateLabel(politica)}
        </Badge>
      ),
    },
    {
      key: 'acoes',
      header: t('humanAccess.col.actions'),
      width: '132px',
      cell: (politica) => (
        <span className={s.acoes} data-testid={`acoes-${politica.id}`}>
          <IconButton
            icon="pen-square"
            label={t(canEdit ? 'humanAccess.row.edit' : 'humanAccess.row.open', {
              name: politica.name,
            })}
            size="sm"
            onClick={() => openDrawer(politica.id)}
          />
          {politica.state === 'ativa' && canDeactivate ? (
            <IconButton
              icon="pause"
              label={t('humanAccess.row.deactivate', { name: politica.name })}
              size="sm"
              onClick={() => setAcao({ tipo: 'desativar', politica })}
            />
          ) : null}
          {politica.state !== 'ativa' && canActivate ? (
            <IconButton
              icon="play"
              label={t('humanAccess.row.activate', { name: politica.name })}
              size="sm"
              disabled={activate.isPending}
              onClick={() => activatePolicy(politica)}
            />
          ) : null}
          {canDelete ? (
            <IconButton
              icon="trash"
              label={t('humanAccess.row.delete', { name: politica.name })}
              size="sm"
              onClick={() => setAcao({ tipo: 'excluir', politica })}
            />
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('humanAccess.page.title')}
        tip={t('humanAccess.page.tip')}
        icon="shield"
        actions={
          canCreate ? (
            <Button icon="plus" onClick={() => openDrawer('nova')}>
              {t('humanAccess.page.new')}
            </Button>
          ) : null
        }
      />

      <Indicators
        ativas={summary.data?.active}
        rascunhos={summary.data?.drafts}
        cobertas={summary.data?.covered_identities}
        capabilities={summary.data?.granted_capabilities}
        regoManual={summary.data?.rego_manual}
      />

      {/*
        Este texto dizia «união» até 03/09, e era mentira no modelo Human ×
        Identity Access: a regra concede do lado da **pessoa**, o contrato
        concede do lado da **identidade**, e o acesso é a interseção dos dois.
        «União» prometia que a regra alarga a identidade — exatamente o erro que
        a reorganização existe para desfazer.

        (O copy de união da T14 / FR-075 é pendência separada, e não se toca.)
      */}
      <Alert tone="info" title={t('humanAccess.combine.title')}>
        {withEmphasis(t('humanAccess.combine.body'))}
      </Alert>

      {activate.error ? (
        <ErrorBanner
          error={activate.error}
          escrita
          title={t('humanAccess.activateError')}
          onRetry={() => activate.reset()}
          retryLabel={t('humanAccess.activateError.close')}
        />
      ) : null}

      <Card bare aria-label={t('humanAccess.table.aria')}>
        <DataTable
          label={t('humanAccess.table.label')}
          columns={columns}
          rows={itens}
          rowKey={(politica) => politica.id}
          loading={lista.isLoading}
          error={lista.error}
          onRetry={() => void lista.refetch()}
          search={filters.q}
          onSearchChange={(q) => swapFilters({ q })}
          searchPlaceholder={t('humanAccess.table.searchPlaceholder')}
          filters={
            <>
              {/* aim-pjv (decisão do mrolim, 10/10/2026): filtro de escolha entre opções é caixa com busca. */}
              <SearchCombobox
                label={t('humanAccess.filter.state')}
                placeholder={t('humanAccess.filter.state.placeholder')}
                searchPlaceholder={t('humanAccess.filter.state.searchPlaceholder')}
                size="sm"
                clearable
                options={[
                  { value: '', label: t('humanAccess.filter.state.all') },
                  { value: 'ativa', label: t('humanAccess.filter.state.active') },
                  { value: 'rascunho', label: t('humanAccess.filter.state.draft') },
                ]}
                value={filters.state}
                onChange={(valor) => swapFilters({ state: (valor as PolicyState) || null })}
              />
              <SearchCombobox
                label={t('humanAccess.filter.mode')}
                placeholder={t('humanAccess.filter.mode.placeholder')}
                searchPlaceholder={t('humanAccess.filter.mode.searchPlaceholder')}
                size="sm"
                clearable
                options={[
                  { value: '', label: t('humanAccess.filter.mode.all') },
                  { value: 'interface', label: t('humanAccess.filter.mode.interface') },
                  { value: 'rego', label: t('humanAccess.filter.mode.rego') },
                ]}
                value={filters.mode}
                onChange={(valor) => swapFilters({ mode: (valor as PolicyMode) || null })}
              />
            </>
          }
          filtersApplied={filtrado}
          onClearFilters={() => setFiltros(FILTERS_INICIAIS)}
          page={filters.page}
          pageSize={PAGE_SIZE}
          total={lista.data?.total}
          onPageChange={(page) => setFiltros((current) => ({ ...current, page }))}
          emptyIcon="shield"
          emptyTitle={t(filtrado ? 'humanAccess.empty.filtered.title' : 'humanAccess.empty.title')}
          emptyMessage={t(
            filtrado ? 'humanAccess.empty.filtered.message' : 'humanAccess.empty.message',
          )}
          emptyAction={
            !filtrado && canCreate ? (
              <Button icon="plus" onClick={() => openDrawer('nova')}>
                {t('humanAccess.page.new')}
              </Button>
            ) : null
          }
        />
      </Card>

      <ConfirmDialog
        open={acao !== null}
        title={
          acao?.tipo === 'excluir'
            ? t('humanAccess.confirm.delete.title', { name: acao.politica.name })
            : t('humanAccess.confirm.draft.title', {
                name: acao?.politica.name ?? t('humanAccess.confirm.thisRule'),
              })
        }
        message={
          acao?.tipo === 'excluir' && acao.politica.state !== 'ativa'
            ? t('humanAccess.confirm.draftNotPublished')
            : t('humanAccess.confirm.stopsApplying')
        }
        impact={
          acao
            ? [
                {
                  label: impactLabel(acao.politica.identities.length),
                  count: acao.politica.identities.length,
                },
              ]
            : undefined
        }
        destructive
        confirmLabel={t(
          acao?.tipo === 'excluir'
            ? 'humanAccess.confirm.delete.label'
            : 'humanAccess.confirm.draft.label',
        )}
        loading={deactivate.isPending || remove.isPending}
        error={
          acao?.tipo === 'excluir' && remove.error ? (
            <ErrorBanner
              error={remove.error}
              escrita
              title={t('humanAccess.deleteError')}
              hideLink
            />
          ) : acao?.tipo === 'desativar' && deactivate.error ? (
            <ErrorBanner
              error={deactivate.error}
              escrita
              title={t('humanAccess.draftError')}
              hideLink
            />
          ) : null
        }
        onCancel={() => {
          setAcao(null);
          deactivate.reset();
          remove.reset();
        }}
        onConfirm={confirmAction}
      />

      <DrawerPresence>
        {drawer.id ? (
          <PolicyDrawer
            key={drawer.id}
            politicaId={drawer.id === 'nova' ? null : drawer.id}
            initialTab={drawerTab}
            validacaoRegoDisponivel={summary.data?.rego_validation_available ?? null}
            onClose={drawer.fechar}
            onSalvo={(mensagem) => {
              toast.mostrar(mensagem, { tone: 'sucesso' });
              drawer.fechar();
            }}
          />
        ) : null}
      </DrawerPresence>
    </>
  );
}
